import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useAuth } from '../../../contexts/AuthContext';
import styles from './MyMercenaries.module.css';

interface Mercenary {
  id: number;
  name: string;
  level: number;
  race: string;
  sex: string;
  avatar: number;
}

const MyMercenaries: React.FC = () => {
  const navigate = useNavigate();
  const { mercenaries, mercenaryId, fetchUserMercenaries, switchMercenary, deleteMercenary } = useMercenary();
  const { user } = useAuth();
  const [canCreate, setCanCreate] = useState(false);
  const [creationMessage, setCreationMessage] = useState('');
  const [dataFetched, setDataFetched] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mercenaryToDelete, setMercenaryToDelete] = useState<Mercenary | null>(null);
  const [deleteNameInput, setDeleteNameInput] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!dataFetched) {
        const response = await fetchUserMercenaries();
        if (response) {
          setCanCreate(response.canCreate);
          setCreationMessage(getCreationMessage(
            response.canCreate,
            response.mercenaries,
            response.isPremiumOrHigher,
            response.dailyLimitReached ?? false,
            response.nextAvailableAt ?? null,
            response.dailyLimit ?? null,
          ));
          setDataFetched(true);
        }
      }
    };
  
    fetchData();
  }, [fetchUserMercenaries, dataFetched]);

  const getCreationMessage = (
    canCreate: boolean,
    mercenaries: Mercenary[],
    isPremiumOrHigher: boolean,
    dailyLimitReached: boolean = false,
    nextAvailableAt: string | null = null,
    dailyLimit: number | null = null,
  ) => {
    if (canCreate) return 'You can create a new mercenary!';
    if (dailyLimitReached) {
      const limitStr = dailyLimit ? ` (limit: ${dailyLimit} per day)` : '';
      if (nextAvailableAt) {
        const diffMs = new Date(nextAvailableAt).getTime() - Date.now();
        if (diffMs > 0) {
          const diffHrs = Math.floor(diffMs / 3600000);
          const diffMins = Math.ceil((diffMs % 3600000) / 60000);
          const timeStr = diffHrs > 0 ? `${diffHrs}h ${diffMins}m` : `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
          return `You have reached your daily mercenary creation limit${limitStr}. Next slot opens in ${timeStr}.`;
        }
      }
      return `You have reached your daily mercenary creation limit${limitStr}. Try again later.`;
    }
  
    if (mercenaries.length === 1) {
      return mercenaries[0].level < 15 && !isPremiumOrHigher
        ? 'Your first mercenary needs to be level 15 or higher to create a second one.'
        : '';
    }
  
    if (mercenaries.length === 2) {
      const [firstMercenary, secondMercenary] = mercenaries;
      const hasRequiredLevels = (firstMercenary.level >= 26 && secondMercenary.level >= 15) || (secondMercenary.level >= 26 && firstMercenary.level >= 15);
      const premiumRequirementMet = isPremiumOrHigher && (firstMercenary.level >= 15 || secondMercenary.level >= 15);
  
      return !hasRequiredLevels && !premiumRequirementMet
        ? 'To create a third mercenary, you need one mercenary at level 26 and another at level 15, or Premium status with one mercenary at level 15.'
        : '';
    }
  
    if (mercenaries.length === 3 && !isPremiumOrHigher) {
      return 'Only Premium users can create a fourth mercenary.';
    }
  
    return 'You have reached the maximum number of mercenaries allowed.';
  };

  const confirmDeleteMercenary = (mercenary: Mercenary) => {
    setMercenaryToDelete(mercenary);
    setDeleteNameInput('');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (mercenaryToDelete && deleteNameInput.toLowerCase() === mercenaryToDelete.name.toLowerCase()) {
      deleteMercenary(mercenaryToDelete.id);
      setShowDeleteConfirm(false);
      setMercenaryToDelete(null);
      setDeleteNameInput('');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setMercenaryToDelete(null);
    setDeleteNameInput('');
  };

  const handleSwitchMercenary = async (mercenary: Mercenary) => {
    const wasDeadBeforeSwitch = user?.isDead;
    await switchMercenary(mercenary);
    // If the user was on the death screen, send them home after clearing death state
    if (wasDeadBeforeSwitch) {
      navigate('/home');
    }
  };

  return (
    <div className={styles.mercenariesContainer}>
      <h2 className={styles.title}>My Mercenaries</h2>
      {mercenaries.length > 0 ? (
        <div className={styles.mercenaryList}>
          {mercenaries.map((mercenary: Mercenary) => (
            <div
              key={mercenary.id}
              className={`${styles.mercenaryCard} ${mercenary.id === mercenaryId ? styles.mercenaryCardActive : ''}`}
              onClick={() => handleSwitchMercenary(mercenary)}
            >
              <div className={styles.cardHeader}>
                <div className={styles.avatarWrapper}>
                  <img
                    src={`/img/avatars/${mercenary.race?.toLowerCase() ?? 'default'}.${mercenary.sex?.toLowerCase() ?? 'default'}.${mercenary.avatar ?? 1}.png`}
                    alt={mercenary.name ?? 'Unknown Mercenary'}
                    className={styles.avatar}
                  />
                </div>
              </div>
              <div className={styles.cardBody}>
                <h3 className={styles.cardName}>{mercenary.name}</h3>
                <div className={styles.cardLevel}>Level <strong>{mercenary.level}</strong></div>
              </div>
              <button
                className={styles.retireButton}
                onClick={(e) => { e.stopPropagation(); confirmDeleteMercenary(mercenary); }}
              >
                Retire
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.noMercenariesMessage}>No mercenaries found. Create one to start your journey!</p>
      )}
      <div className={styles.creationSection}>
        <button
          className={styles.createButton}
          onClick={() => navigate('/create-mercenary')}
          disabled={!canCreate}
        >
          Create New Mercenary
        </button>
        <p className={styles.creationMessage}>{creationMessage}</p>
      </div>

      {/* Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmModal}>
            <h3>Retire Mercenary</h3>
            <p>This action is permanent. Type <strong className={styles.mercName}>{mercenaryToDelete?.name}</strong> to confirm.</p>
            <input
              className={styles.deleteNameInput}
              type="text"
              value={deleteNameInput}
              onChange={(e) => setDeleteNameInput(e.target.value)}
              placeholder="Enter mercenary name"
              autoFocus
            />
            <div className={styles.modalButtons}>
              <button
                className={styles.confirmButton}
                onClick={handleDeleteConfirm}
                disabled={deleteNameInput.toLowerCase() !== mercenaryToDelete?.name.toLowerCase()}
              >
                Retire
              </button>
              <button className={styles.cancelButton} onClick={handleDeleteCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMercenaries;
